import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { PublishedQuestionnaireGroupPartsDocument } from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { QuestionnaireRunner } from "@/components/grow/QuestionnaireRunner";

// One part of a questionnaire group: meta from the group, answering via the
// shared runner (genesis#183, spark#196). After submit the group's answered flags
// are refetched so the overview reflects the new state immediately.
//
// Where to go after submitting depends on the group's shape. A group with a
// single part has no overview - the user was sent straight here - so returning to
// it would bounce them back through a redirect; they go to the catalogue instead.

export function QuestionnaireGroupPartPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { questionnaireGroupId = "", questionnaireId = "" } = useParams();

  const { data, loading } = useQuery(PublishedQuestionnaireGroupPartsDocument);
  const group = (data?.publishedQuestionnaireGroups ?? []).find(
    (g) => g.questionnaireGroupId === questionnaireGroupId,
  );
  const part = group?.parts.find((p) => p.questionnaire.questionnaireId === questionnaireId);
  const singlePart = group?.parts.length === 1;

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="PublishedQuestionnaireGroupParts, QuestionnaireQuestions, SaveQuestionnaireResults"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      {loading && !part ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : !part ? (
        // The group was withdrawn, the part deactivated, or the link is stale.
        // Either way genesis no longer serves it, and the empty state is the
        // honest answer - the runner would otherwise ask for questions that are
        // not there.
        <NoBackend
          label={intl.formatMessage({
            id: "grow.play.group.missing",
            defaultMessage: "This questionnaire is not available on this environment.",
          })}
        />
      ) : (
        <QuestionnaireRunner
          questionnaireId={questionnaireId}
          name={part.questionnaire.name}
          instructions={part.questionnaire.instructions}
          eyebrow={<FormattedMessage id="grow.play.quiz.eyebrow" defaultMessage="Grow · Play" />}
          onSubmitted={() => {
            void client.refetchQueries({
              include: [PublishedQuestionnaireGroupPartsDocument],
            });
            navigate(singlePart ? "/grow/play" : `/grow/play/group/${questionnaireGroupId}`);
          }}
        />
      )}
    </section>
  );
}
